import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Building2,
  User,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  Wallet,
  Receipt,
  Plus,
  Loader2,
  Calendar,
  AlertCircle,
  FileText,
  PiggyBank,
  Edit2,
  ChevronRight,
  Trash2,
  Tag,
} from "lucide-react";
import CustomerStatementModal from "@/components/CustomerStatementModal";
import CustomerDiscountModal from "@/components/CustomerDiscountModal";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import Pagination from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/format";
import { JOB_STATUS_LABELS, jobStatusTone } from "@/lib/constants";
import { useAuth, hasRole } from "@/contexts/AuthContext";

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = hasRole(user, "admin", "sales");
  const isAdmin = hasRole(user, "admin");

  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  // Associated jobs state
  const [jobs, setJobs] = useState([]);
  const [jobsTotal, setJobsTotal] = useState(0);
  const [jobsPage, setJobsPage] = useState(1);
  const [jobsLoading, setJobsLoading] = useState(true);

  // Edit Customer Dialog
  const [editOpen, setEditOpen] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editForm, setEditForm] = useState({});

  // Statement & Discount Modals State
  const [statementOpen, setStatementOpen] = useState(false);
  const [discountOpen, setDiscountOpen] = useState(false);
  const [transferDiscount, setTransferDiscount] = useState(null);
  const [transferJobId, setTransferJobId] = useState("");
  const [transferring, setTransferring] = useState(false);
  const [discounts, setDiscounts] = useState([]);
  const [discountsLoading, setDiscountsLoading] = useState(false);

  const loadDiscounts = useCallback(async () => {
    setDiscountsLoading(true);
    try {
      const res = await api.get(`/customers/${id}/discounts`);
      setDiscounts(Array.isArray(res.data) ? res.data : (res.data?.items || []));
    } catch {
      // Non-blocking
    } finally {
      setDiscountsLoading(false);
    }
  }, [id]);

  const handleTransferDiscount = async (e) => {
    e.preventDefault();
    if (!transferDiscount || !transferJobId || transferring) return;
    setTransferring(true);
    try {
      await api.post(`/customers/${id}/discounts/${transferDiscount.id}/transfer`, {
        job_id: transferJobId,
      });
      toast.success("Cari iskonto seçilen işe başarıyla aktarıldı.");
      setTransferDiscount(null);
      setTransferJobId("");
      loadCustomer();
      loadJobs(jobsPage);
      loadDiscounts();
    } catch (err) {
      toast.error(formatApiError(err, "İskonto işe aktarılamadı."));
    } finally {
      setTransferring(false);
    }
  };

  const handleCancelDiscount = async (discount) => {
    if (!canEdit) return;
    const reason = window.prompt(
      `'${formatCurrency(discount.amount)}' tutarındaki iskontoyu iptal etmek istediğinize emin misiniz? Lütfen iptal gerekçesini belirtin:`
    );
    if (!reason || !reason.trim()) {
      toast.error("İptal gerekçesi girilmediğinden işlem yapılmadı.");
      return;
    }

    try {
      await api.post(`/customers/${id}/discounts/${discount.id}/cancel`, {
        cancel_reason: reason.trim(),
      });
      toast.success("İskonto kaydı başarıyla iptal edildi.");
      loadCustomer();
      loadDiscounts();
    } catch (err) {
      toast.error(formatApiError(err, "İskonto iptal edilemedi."));
    }
  };

  const handleDeleteCustomer = async () => {
    if (!isAdmin || !customer) return;
    const hasJobs = (customer.jobs_count || 0) > 0 || jobsTotal > 0;
    if (hasJobs) {
      toast.error(
        `Bu müşteriye bağlı iş kayıtları bulunmaktadır. Geçmişi olan müşteriler silinemez; carinin durumunu pasife alabilir veya Müşteri Birleştirme ekranını kullanabilirsiniz.`
      );
      return;
    }

    if (!window.confirm(`'${customer.display_name}' (${customer.customer_number}) adlı müşteriyi KALICI OLARAK silmek istediğinize emin misiniz?`)) {
      return;
    }

    const confirmText = window.prompt("Lütfen silme işlemini onaylamak için büyük harflerle 'SİL' yazın:");
    if (confirmText !== "SİL") {
      toast.error("Silme işlemi onaylanmadı.");
      return;
    }

    setDeleting(true);
    try {
      const res = await api.delete(`/customers/${customer.id}`);
      toast.success(res.data?.message || "Müşteri kaydı kalıcı olarak silindi.");
      navigate("/musteriler");
    } catch (err) {
      toast.error(formatApiError(err, "Müşteri silinemedi."));
    } finally {
      setDeleting(false);
    }
  };

  const loadCustomer = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/customers/${id}`);
      setCustomer(res.data);
      setEditForm({
        customer_type: res.data.customer_type || "individual",
        display_name: res.data.display_name || "",
        company_name: res.data.company_name || "",
        phone: res.data.phone || "",
        alternative_phone: res.data.alternative_phone || "",
        email: res.data.email || "",
        city: res.data.city || "",
        district: res.data.district || "",
        address: res.data.address || "",
        notes: res.data.notes || "",
        is_active: res.data.is_active,
      });
    } catch (err) {
      toast.error(formatApiError(err));
      navigate("/musteriler");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const loadJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const res = await api.get(`/customers/${id}/jobs`, {
        params: { page: jobsPage, page_size: 15 },
      });
      setJobs(res.data.items || []);
      setJobsTotal(res.data.total || 0);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setJobsLoading(false);
    }
  }, [id, jobsPage]);

  useEffect(() => {
    loadCustomer();
  }, [loadCustomer]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    loadDiscounts();
  }, [loadDiscounts]);

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      await api.patch(`/customers/${id}`, editForm);
      toast.success("Müşteri bilgileri güncellendi.");
      setEditOpen(false);
      loadCustomer();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingEdit(false);
    }
  };

  if (loading) {
    return (
      <div className="p-12 flex flex-col items-center justify-center text-slate-500">
        <Loader2 size={32} className="animate-spin mb-3 text-[#FF353A]" />
        <p className="text-sm">Müşteri detayları yükleniyor...</p>
      </div>
    );
  }

  if (!customer) return null;

  const fin = customer.financial_summary || {};
  const netOutstanding = Math.max(
    0,
    parseFloat(
      fin.net_balance !== undefined
        ? fin.net_balance
        : fin.net_outstanding !== undefined
        ? fin.net_outstanding
        : fin.outstanding_receivables || 0
    )
  );

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      {/* Breadcrumb & Navigation */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/musteriler")}
            className="text-slate-600 hover:text-[#171A1F] -ml-2"
          >
            <ArrowLeft size={16} className="mr-1.5" />
            Müşteriler
          </Button>
          <span className="text-slate-300">/</span>
          <span className="text-xs font-mono font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
            {customer.customer_number}
          </span>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setStatementOpen(true)}
            className="border-slate-300 text-slate-800 hover:bg-slate-100"
            data-testid="btn-customer-statement"
          >
            <FileText size={14} className="mr-1.5 text-slate-600" />
            Cari Hesap Ekstresi
          </Button>

          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDiscountOpen(true)}
              className="border-purple-200 text-purple-700 hover:bg-purple-50 hover:text-purple-800"
              data-testid="btn-add-discount"
            >
              <Tag size={14} className="mr-1.5" />
              İskonto Uygula
            </Button>
          )}

          {isAdmin && (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDeleteCustomer}
              disabled={deleting}
              data-testid="btn-delete-customer"
            >
              {deleting ? (
                <Loader2 size={14} className="mr-1.5 animate-spin" />
              ) : (
                <Trash2 size={14} className="mr-1.5" />
              )}
              Sil
            </Button>
          )}
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditOpen(true)}
              className="border-[#DCE2E8] text-slate-700"
              data-testid="btn-edit-customer"
            >
              <Edit2 size={14} className="mr-1.5" />
              Düzenle
            </Button>
          )}
          {canEdit && (
            <Button
              size="sm"
              onClick={() => navigate(`/isler/yeni?customer_id=${customer.id}`)}
              className="bg-[#FF353A] hover:bg-[#E0262B] text-white"
              data-testid="btn-create-job-for-customer"
            >
              <Plus size={14} className="mr-1.5" />
              Bu Müşteriye İş Aç
            </Button>
          )}
        </div>
      </div>

      {/* Customer Header Info */}
      <div className="rounded-lg border border-[#DCE2E8] bg-white p-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 shrink-0 border border-slate-200">
              {customer.customer_type === "corporate" ? (
                <Building2 size={24} className="text-blue-600" />
              ) : (
                <User size={24} className="text-slate-700" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2.5">
                <h1 className="text-2xl font-bold text-[#171A1F]">{customer.display_name}</h1>
                <StatusBadge
                  tone={customer.is_active ? "success" : "neutral"}
                  label={customer.is_active ? "Aktif" : "Pasif"}
                />
              </div>
              {customer.company_name && customer.company_name !== customer.display_name && (
                <p className="text-sm text-slate-600 mt-0.5 font-medium">{customer.company_name}</p>
              )}
              <div className="flex flex-wrap items-center gap-y-1 gap-x-4 text-xs text-slate-500 mt-2">
                {customer.phone && (
                  <span className="flex items-center gap-1 font-mono text-slate-700">
                    <Phone size={13} className="text-slate-400" />
                    {customer.phone}
                  </span>
                )}
                {customer.email && (
                  <span className="flex items-center gap-1">
                    <Mail size={13} className="text-slate-400" />
                    {customer.email}
                  </span>
                )}
                {(customer.city || customer.district) && (
                  <span className="flex items-center gap-1">
                    <MapPin size={13} className="text-slate-400" />
                    {[customer.district, customer.city].filter(Boolean).join(", ")}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Quick contact / address card */}
          {customer.address && (
            <div className="text-xs text-slate-600 max-w-sm bg-slate-50 p-3 rounded border border-slate-100">
              <span className="font-semibold text-slate-700 block mb-1">Adres:</span>
              <p className="whitespace-pre-line leading-relaxed">{customer.address}</p>
            </div>
          )}
        </div>

        {customer.notes && (
          <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-600 flex items-start gap-2">
            <FileText size={14} className="text-slate-400 shrink-0 mt-0.5" />
            <span><strong className="text-slate-700">Notlar:</strong> {customer.notes}</span>
          </div>
        )}
      </div>

      {/* Financial Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="customer-kpis">
        <Card className="border-[#DCE2E8] shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-blue-50 text-blue-600 shrink-0">
              <Briefcase size={20} />
            </div>
            <div className="min-w-0">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide block">
                Toplam İş Bedeli
              </span>
              <div className="text-lg font-bold text-[#171A1F] mt-0.5 truncate font-mono">
                {formatCurrency(fin.total_sales || 0)}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#DCE2E8] shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-emerald-50 text-emerald-600 shrink-0">
              <Wallet size={20} />
            </div>
            <div className="min-w-0">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide block">
                Toplam Tahsilat
              </span>
              <div className="text-lg font-bold text-emerald-700 mt-0.5 truncate font-mono">
                {formatCurrency(fin.total_collections || 0)}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#DCE2E8] shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-purple-50 text-purple-600 shrink-0">
              <Tag size={20} />
            </div>
            <div className="min-w-0">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide block">
                Toplam İskonto
              </span>
              <div className="text-lg font-bold text-purple-700 mt-0.5 truncate font-mono" data-testid="kpi-total-discounts">
                {formatCurrency(fin.total_discounts || 0)}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#DCE2E8] shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-red-50 text-[#FF353A] shrink-0">
              <Receipt size={20} />
            </div>
            <div className="min-w-0">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide block">
                Kalan Alacak
              </span>
              <div className="text-lg font-bold text-[#FF353A] mt-0.5 truncate font-mono" data-testid="kpi-net-outstanding">
                {formatCurrency(Math.max(0, parseFloat(fin.net_balance ?? fin.outstanding_receivables ?? 0)))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#DCE2E8] shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-slate-100 text-slate-700 shrink-0">
              {parseFloat(fin.net_balance || 0) < 0 ? (
                <PiggyBank size={20} className="text-amber-600" />
              ) : (
                <AlertCircle size={20} className="text-slate-600" />
              )}
            </div>
            <div className="min-w-0">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide block">
                {parseFloat(fin.net_balance || 0) < 0 ? "Müşteri Avansı" : "Net Bakiye"}
              </span>
              <div className={`text-lg font-bold mt-0.5 truncate font-mono ${
                parseFloat(fin.net_balance || 0) > 0
                  ? "text-[#FF353A]"
                  : parseFloat(fin.net_balance || 0) < 0
                  ? "text-amber-700"
                  : "text-slate-700"
              }`}>
                {parseFloat(fin.net_balance || 0) < 0
                  ? formatCurrency(-parseFloat(fin.net_balance))
                  : formatCurrency(fin.net_balance || 0)}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Associated Jobs Section */}
      <div className="rounded-lg border border-[#DCE2E8] bg-white shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-[#DCE2E8] flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-[#171A1F]">Bağlı İşler</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Bu müşteriye ait tüm iş kayıtları, sözleşme bedelleri ve tahsilat durumları.
            </p>
          </div>
          <span className="text-xs font-semibold px-2.5 py-1 rounded bg-slate-100 text-slate-700">
            Toplam {jobsTotal} İş
          </span>
        </div>

        {jobsLoading ? (
          <div className="p-10 flex flex-col items-center justify-center text-slate-500">
            <Loader2 size={24} className="animate-spin mb-2 text-[#FF353A]" />
            <p className="text-xs">İşler listeleniyor...</p>
          </div>
        ) : jobs.length === 0 ? (
          <EmptyState
            icon={Briefcase}
            title="Kayıtlı İş Bulunamadı"
            description="Bu müşteriye ait henüz bir iş kaydı oluşturulmamış."
            actionLabel={canEdit ? "Bu Müşteriye Yeni İş Aç" : undefined}
            onAction={canEdit ? () => navigate(`/isler/yeni?customer_id=${customer.id}`) : undefined}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="w-28 font-semibold text-slate-700">İş No</TableHead>
                    <TableHead className="font-semibold text-slate-700">Durum</TableHead>
                    <TableHead className="font-semibold text-slate-700">Sözleşme Tarihi</TableHead>
                    <TableHead className="text-right font-semibold text-slate-700">Satış Tutarı</TableHead>
                    <TableHead className="text-right font-semibold text-slate-700">Tahsilat</TableHead>
                    <TableHead className="text-right font-semibold text-slate-700">Kalan Bakiye</TableHead>
                    <TableHead className="font-semibold text-slate-700 text-center">Fiyatlandırma</TableHead>
                    <TableHead className="w-20" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jobs.map((job) => {
                    const salesAmt = parseFloat(job.sales_amount || 0);
                    const collAmt = parseFloat(job.collected_amount || 0);
                    const discAmt = parseFloat(job.discount_amount || 0);
                    const remBalance = Math.max(0, salesAmt - collAmt - discAmt);
                    const isLegacy = job.pricing_mode === "LEGACY";

                    return (
                      <TableRow
                        key={job.id}
                        onClick={() => navigate(`/isler/${job.id}`)}
                        className="cursor-pointer hover:bg-slate-50/80 transition-colors"
                        data-testid={`customer-job-row-${job.id}`}
                      >
                        <TableCell className="font-mono text-xs font-bold text-[#171A1F]">
                          {job.job_number}
                        </TableCell>
                        <TableCell>
                          <StatusBadge
                            tone={jobStatusTone(job.status)}
                            label={JOB_STATUS_LABELS[job.status] || job.status}
                          />
                        </TableCell>
                        <TableCell className="text-xs text-slate-600">
                          {job.contract_date ? formatDate(job.contract_date) : "-"}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs text-slate-700">
                          {formatCurrency(salesAmt)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs text-emerald-700 font-medium">
                          {formatCurrency(collAmt)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs font-semibold">
                          <span className={remBalance > 0 ? "text-[#FF353A]" : "text-slate-500"}>
                            {formatCurrency(remBalance)}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${
                            isLegacy
                              ? "bg-amber-50 text-amber-800 border border-amber-200"
                              : "bg-emerald-50 text-emerald-800 border border-emerald-200"
                          }`}>
                            {isLegacy ? "LEGACY" : "KALEM BAZLI"}
                          </span>
                        </TableCell>
                        <TableCell className="text-right pr-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/isler/${job.id}`);
                            }}
                            className="text-xs text-slate-500 hover:text-[#171A1F] h-7 px-2"
                          >
                            Görüntüle
                            <ChevronRight size={14} className="ml-1" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {jobsTotal > 15 && (
              <div className="p-4 border-t border-[#DCE2E8]">
                <Pagination
                  page={jobsPage}
                  pageSize={15}
                  total={jobsTotal}
                  onPageChange={setJobsPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Customer Discounts Section */}
      <div className="rounded-lg border border-[#DCE2E8] bg-white shadow-sm overflow-hidden" data-testid="customer-discounts-section">
        <div className="px-6 py-4 border-b border-[#DCE2E8] flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="flex items-center gap-2">
              <Tag size={18} className="text-purple-600" />
              <h2 className="text-lg font-bold text-[#171A1F]">Uygulanan Cari İskontolar</h2>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              İş sözleşmelerini değiştirmeksizin müşterinin cari alacak bakiyesinden doğrudan düşülen iskontolar.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold px-2.5 py-1 rounded bg-purple-50 text-purple-700">
              {discounts.filter((d) => !d.is_cancelled && d.status !== "cancelled" && d.status !== "transferred").length} Aktif İskonto
            </span>
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDiscountOpen(true)}
                className="border-purple-200 text-purple-700 hover:bg-purple-50 text-xs"
              >
                <Plus size={13} className="mr-1" />
                İskonto Uygula
              </Button>
            )}
          </div>
        </div>

        {discountsLoading ? (
          <div className="p-10 flex flex-col items-center justify-center text-slate-500">
            <Loader2 size={24} className="animate-spin mb-2 text-purple-600" />
            <p className="text-xs">İskonto geçmişi yükleniyor...</p>
          </div>
        ) : discounts.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <Tag size={28} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm font-medium text-slate-700">Tanımlı İskonto Bulunmuyor</p>
            <p className="text-xs text-slate-500 mt-0.5">
              Bu müşteriye ait aktif veya iptal edilmiş bir cari iskonto kaydı yoktur.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="w-32 font-semibold text-slate-700">Tarih</TableHead>
                  <TableHead className="w-36 text-right font-semibold text-slate-700">İskonto Tutarı</TableHead>
                  <TableHead className="font-semibold text-slate-700">Açıklama</TableHead>
                  <TableHead className="font-semibold text-slate-700">Ekleyen</TableHead>
                  <TableHead className="w-32 font-semibold text-slate-700 text-center">Durum</TableHead>
                  {isAdmin && <TableHead className="w-24 text-right font-semibold text-slate-700">İşlem</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {discounts.map((d) => {
                  const isCancelled = d.is_cancelled || d.status === "cancelled";
                  const isTransferred = d.status === "transferred";
                  const isInactive = isCancelled || isTransferred;
                  return (
                  <TableRow
                    key={d.id}
                    className={`hover:bg-slate-50/80 ${isInactive ? "opacity-60 bg-slate-50/40" : ""}`}
                  >
                    <TableCell className="font-mono text-xs text-slate-700">
                      {formatDate(d.discount_date)}
                    </TableCell>
                    <TableCell
                      className={`text-right font-mono font-semibold text-sm ${
                        isInactive ? "line-through text-slate-400" : "text-purple-700"
                      }`}
                    >
                      {formatCurrency(d.amount)}
                    </TableCell>
                    <TableCell className="text-xs text-slate-700 max-w-xs truncate">
                      {d.description || "-"}
                      {isCancelled && d.cancel_reason && (
                        <div className="text-[11px] text-rose-600 mt-0.5 italic">
                          İptal Nedeni: {d.cancel_reason}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">
                      <div>{d.created_by_name || d.creator_name || "-"}</div>
                      <div className="text-[11px] text-slate-400 font-mono">
                        {d.created_at ? formatDate(d.created_at) : ""}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {isCancelled ? (
                        <Badge variant="outline" className="border-rose-200 text-rose-700 bg-rose-50 text-[11px]">
                          İptal Edildi
                        </Badge>
                      ) : isTransferred ? (
                        <Badge variant="outline" className="border-blue-200 text-blue-700 bg-blue-50 text-[11px]">
                          İşe Aktarıldı
                        </Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[11px]">
                          Aktif
                        </Badge>
                      )}
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        {!isInactive && (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setTransferDiscount(d); setTransferJobId(""); }}
                              className="text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-7 px-2"
                              title="İskontoyu Bir İşe Aktar"
                            >
                              İşe Aktar
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCancelDiscount(d)}
                              className="text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50 h-7 px-2"
                              title="İskontoyu İptal Et"
                            >
                              İptal Et
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Edit Customer Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Müşteri Bilgilerini Düzenle</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveEdit} className="space-y-3.5 mt-2">
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Müşteri Türü</label>
              <Select
                value={editForm.customer_type}
                onValueChange={(v) => setEditForm((prev) => ({ ...prev, customer_type: v }))}
              >
                <SelectTrigger className="border-[#DCE2E8]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">Bireysel Müşteri</SelectItem>
                  <SelectItem value="corporate">Kurumsal / Firma</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Müşteri Adı Soyadı *</label>
              <Input
                required
                value={editForm.display_name}
                onChange={(e) => setEditForm((prev) => ({ ...prev, display_name: e.target.value }))}
                className="border-[#DCE2E8]"
                data-testid="input-edit-cust-name"
              />
            </div>

            {editForm.customer_type === "corporate" && (
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Firma / Şirket Ünvanı</label>
                <Input
                  value={editForm.company_name}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, company_name: e.target.value }))}
                  className="border-[#DCE2E8]"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Telefon</label>
                <Input
                  value={editForm.phone}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, phone: e.target.value }))}
                  className="border-[#DCE2E8]"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Alternatif Telefon</label>
                <Input
                  value={editForm.alternative_phone}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, alternative_phone: e.target.value }))}
                  className="border-[#DCE2E8]"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">E-Posta</label>
                <Input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))}
                  className="border-[#DCE2E8]"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Durum</label>
                <Select
                  value={editForm.is_active ? "true" : "false"}
                  onValueChange={(v) => setEditForm((prev) => ({ ...prev, is_active: v === "true" }))}
                >
                  <SelectTrigger className="border-[#DCE2E8]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Aktif</SelectItem>
                    <SelectItem value="false">Pasif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Şehir</label>
                <Input
                  value={editForm.city}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, city: e.target.value }))}
                  className="border-[#DCE2E8]"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">İlçe</label>
                <Input
                  value={editForm.district}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, district: e.target.value }))}
                  className="border-[#DCE2E8]"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Açık Adres</label>
              <Textarea
                rows={2}
                value={editForm.address}
                onChange={(e) => setEditForm((prev) => ({ ...prev, address: e.target.value }))}
                className="border-[#DCE2E8]"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Notlar</label>
              <Textarea
                rows={2}
                value={editForm.notes}
                onChange={(e) => setEditForm((prev) => ({ ...prev, notes: e.target.value }))}
                className="border-[#DCE2E8]"
              />
            </div>

            <DialogFooter className="mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditOpen(false)}
                className="border-[#DCE2E8]"
              >
                İptal
              </Button>
              <Button
                type="submit"
                disabled={savingEdit}
                className="bg-[#FF353A] hover:bg-[#E0262B] text-white"
                data-testid="btn-save-edit-cust"
              >
                {savingEdit ? <Loader2 size={16} className="animate-spin mr-1" /> : null}
                Güncelle
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Transfer Legacy Customer Discount to Job Modal */}
      <Dialog open={!!transferDiscount} onOpenChange={(o) => { if (!o) setTransferDiscount(null); }}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Cari İskontoyu İşe Aktar</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleTransferDiscount} className="space-y-4 mt-2">
            <p className="text-xs text-slate-600">
              {transferDiscount && formatCurrency(transferDiscount.amount)} tutarındaki cari iskonto seçeceğiniz işe aktarılacak ve cari iskonto kaydı pasife alınacaktır.
            </p>
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">Hedef İş *</label>
              <Select value={transferJobId} onValueChange={setTransferJobId}>
                <SelectTrigger className="border-[#DCE2E8]">
                  <SelectValue placeholder="İş seçin..." />
                </SelectTrigger>
                <SelectContent>
                  {jobs.filter(j => !j.is_archived).map(j => (
                    <SelectItem key={j.id} value={j.id}>
                      {j.job_number} ({formatCurrency(Math.max(0, parseFloat(j.sales_amount || 0) - parseFloat(j.collected_amount || 0) - parseFloat(j.discount_amount || 0)))} kalan)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTransferDiscount(null)}>Vazgeç</Button>
              <Button type="submit" disabled={!transferJobId || transferring} className="bg-purple-600 hover:bg-purple-700 text-white">
                {transferring ? "Aktarılıyor..." : "İşe Aktar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Customer Statement Modal */}
      <CustomerStatementModal
        open={statementOpen}
        onOpenChange={setStatementOpen}
        customerId={customer.id}
        customerName={customer.display_name}
        customer={customer}
      />

      {/* Customer Discount Modal */}
      <CustomerDiscountModal
        open={discountOpen}
        onOpenChange={setDiscountOpen}
        customerId={customer.id}
        customerName={customer.display_name}
        maxAvailable={netOutstanding}
        customer={customer}
        onSuccess={() => {
          loadCustomer();
          loadDiscounts();
        }}
      />
    </div>
  );
}
